/* ============================================================================
 *  siteConfig.js — TestDrones
 *  Niche : Piloto de Drones (UAS) A1/A3 y A2 — AESA (España / EASA)
 * ============================================================================ */

export const siteConfig = {
  saasKey: "test-drones-es",
  brand: {
    name: "TestDrones",
    namePrefix: "Test",
    nameSuffix: "Drones",
    subBrand: "by MIVX",
    tagline:
      "Tu plataforma de preparación para los certificados oficiales de Piloto de Drones (A1/A3 y A2) de AESA.",
    flagEmoji: "🇪🇸",
    madeInLabel: "Normativa EASA - España",
    locale: "es-ES",
    timezone: "Europe/Madrid",
    socialLinks: [],
  },

  niche: {
    professionLabel: "Piloto de Drones (Categoría Abierta)",
    aspirantLabel: "Aspirante a Piloto UAS",
    country: "España",
    authority: "Agencia Estatal de Seguridad Aérea (AESA)",
    phonePlaceholder: "+34 600 000 000",
    flagshipLaw: "Reglamento de Ejecución (UE) 2019/947",
  },

  // Referencia oficial al programa (proof-as-layout) mostrada bajo el H1 del Hero.
  officialReference:
    "Temario oficial EASA · Categoría Abierta (Subcategorías A1/A3 y A2)",

  // Labels de los sellos
  sealLabels: {
    verified: "Verificado",
    official: "Normativa Oficial",
    source: "Fuente: AESA / EASA",
  },

  contact: {
    supportEmail: "go@mivx.com",
    salesEmail: "go@mivx.com",
  },

  payment: {
    checkoutUrl: "https://buy.stripe.com/8x2bJ0cfWe6Z97ocxB0co1g", // URL de Stripe para este nuevo dominio
    portalUrl: "",
    cancelSubscriptionEndpoint:
      "https://fgsacxsmkccogatuhofn.supabase.co/functions/v1/cancelar-suscripcion",
    providerName: "Stripe",
  },

  api: {
    geminiModel: "gemini-2.5-flash",
    aiErrorFallback:
      "No se pudo conectar con el Instructor IA. Por favor, intenta más tarde.",
  },

  seo: {
    title: "Test Piloto de Drones AESA 2026 — Simulacros A1/A3 y A2",
    description:
      "Prepara el examen oficial de piloto de drones de AESA (EASA). Banco de preguntas actualizadas, normativa europea y simulacros reales. Empieza gratis.",
    hiddenH1: "Test Certificado Piloto de Drones AESA 2026: A1/A3 y A2",
    hiddenP:
      "Plataforma de preparación para la certificación de piloto a distancia de UAS con temario oficial europeo, reglas de vuelo y simulacros tipo examen.",
    keywords:
      "test piloto drones, examen aesa drones, a1 a3 aesa, examen a2 drones, simulacro drones españa, certificado uas, normativa easa drones",
    author: "TestDrones",
    domain: "testdronesespana.com",
    canonicalUrl: "https://testdronesespana.com/",
    ogImage: "/og-image-drones.png",
    ogTitle: "TestDrones — Exámenes y Simulacros de Piloto de Drones AESA",
    ogDescription:
      "Domina la normativa EASA con simulacros idénticos al examen oficial de AESA para las categorías A1/A3 y A2. Empieza a practicar gratis.",
    schemaCourseName: "Curso de Preparación Certificado Piloto de Drones AESA",
    schemaCourseDescription:
      "Simulador interactivo y banco de preguntas para los certificados oficiales de piloto de UAS (A1/A3 y A2) regulados por AESA y EASA.",
  },

  // Colores adaptados al sector Aviación/Tecnología (Blues y Sky)
  colors: {
    primary: "#2563eb", // blue-600
    primaryDark: "#1d4ed8", // blue-700
    accent: "#0ea5e9", // sky-500
    primaryTw: "blue-600",
    primaryTwHover: "blue-700",
    primaryTwLight: "blue-50",
    primaryTwBorder: "blue-200",
    primaryTwText: "blue-800",
    secondaryTw: "sky-500",
    secondaryTwLight: "sky-50",
    accentTw: "sky-400",
    gradientFrom: "from-blue-600",
    gradientTo: "to-sky-500",
  },

  stats: [
    { value: "+800", label: "Preguntas de normativa EASA/AESA" },
    { value: "100%", label: "Actualizado al marco regulatorio 2026" },
    { value: "A1/A3 y A2", label: "Subcategorías cubiertas" },
    { value: "Ilimitados", label: "Simulacros tipo examen oficial" },
  ],

  hero: {
    badge: "Simulador con formato oficial AESA",
    titleLine1: "Consigue tu certificado oficial de",
    titleLine2: "Piloto de Drones A1/A3 y A2",
    subtitle:
      "Aprueba a la primera los exámenes de AESA con nuestro banco de preguntas actualizado a la normativa europea (EASA). Practica con simulacros idénticos al examen real y resuelve tus dudas de vuelo con un Instructor IA.",
    primaryCta: "Comienza gratis",
    secondaryCta: "Hacer test de prueba",
    secondaryCtaBadge: "Gratis",
  },

  features: {
    sectionEyebrow: "Características",
    sectionTitle: "Todo el espacio aéreo en una sola plataforma",
    sectionSubtitle:
      "Diseñada específicamente para superar los exámenes de AESA. Cubrimos desde reglamentación básica hasta procedimientos operacionales y mitigación de riesgos.",
    items: [
      {
        title: "Normativa EASA Actualizada",
        description:
          "Preguntas basadas estrictamente en el Reglamento de Ejecución (UE) 2019/947 y la normativa nacional española aplicable.",
        color: "from-blue-500 to-sky-500",
        iconKey: "book",
        iconPath:
          "M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253",
      },
      {
        title: "Simulador de Examen Real",
        description:
          "Enfréntate a simulacros de 40 preguntas (A1/A3) o 30 preguntas (A2) con límite de tiempo, calcados al portal de AESA.",
        color: "from-indigo-500 to-blue-500",
        iconKey: "clipboard",
        iconPath:
          "M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z",
      },
      {
        title: "Módulos Específicos",
        description:
          "Practica por separado: meteorología, rendimiento del UAS, privacidad y protección de datos, o procedimientos operacionales.",
        color: "from-sky-500 to-blue-500",
        iconKey: "target",
        iconPath:
          "M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01",
      },
      {
        title: "Respuestas Justificadas",
        description:
          "Cada fallo viene acompañado de una explicación citando el artículo exacto del reglamento europeo para que aprendas el porqué.",
        color: "from-blue-500 to-indigo-500",
        iconKey: "bulb",
        iconPath:
          "M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z",
      },
      {
        title: "Métricas de Vuelo",
        description:
          "Monitoriza tu rendimiento en cada categoría. Descubre si fallas más en seguridad aérea o en lectura de cartas aeronáuticas.",
        color: "from-sky-500 to-indigo-500",
        iconKey: "chart",
        iconPath:
          "M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z",
      },
      {
        title: "Instructor IA",
        description:
          "¿No entiendes la diferencia entre VLOS y BVLOS? Pregúntale a nuestro bot especializado en normativa UAS que está disponible 24/7.",
        color: "from-blue-500 to-sky-500",
        iconKey: "spark",
        iconPath: "M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z",
      },
    ],
  },

  demo: {
    sectionEyebrow: "Ponte a prueba hoy",
    sectionTitlePart1: "Test Oficial de",
    sectionTitlePart2: "Categoría Abierta",
    sectionSubtitle:
      "Experimenta preguntas reales de las convocatorias de AESA. Sin registros ni tarjetas.",
    bullets: [
      { label: "Normativa EASA", icon: "🇪🇺", iconKey: "document-check" },
      { label: "Tiempo real", icon: "⏱️", iconKey: "clock" },
      { label: "Calificación inmediata", icon: "📊", iconKey: "chart" },
      { label: "Explicaciones de vuelo", icon: "💡", iconKey: "bulb" },
    ],
    ctaLabel: "Comenzar Test",
    ctaBadge: "¡GRATIS!",
    answerCorrectLabel: "Correcto, piloto",
    answerIncorrectLabel: "Respuesta incorrecta",
    // Preguntas actualizadas a normativa de Drones
    questions: [
      {
        q: "¿Cuál es la altura máxima permitida de vuelo en la categoría abierta (A1/A3 y A2) respecto al punto más próximo en la superficie terrestre?",
        o: ["50 metros", "100 metros", "120 metros", "150 metros"],
        c: 2,
        exp: "En la categoría abierta (A1/A3 y A2), la altura máxima de vuelo está restringida a 120 metros desde el punto más próximo en la superficie de la tierra, salvo que se vuele sobre un obstáculo más alto a petición de su propietario.",
      },
      {
        q: "En las operaciones con UAS, ¿qué significan las siglas VLOS?",
        o: [
          "Vehicle Line of Sight",
          "Visual Line of Sight (Línea de vista visual)",
          "Vertical Landing Open System",
          "Visual Location Operating System",
        ],
        c: 1,
        exp: "VLOS significa 'Visual Line of Sight'. Es el tipo de operación en la que el piloto a distancia mantiene contacto visual directo con el dron en todo momento para evitar colisiones.",
      },
      {
        q: "Según la normativa europea, para volar un dron de menos de 250 gramos (Clase C0) con cámara, el piloto debe...",
        o: [
          "No requiere hacer nada, es un juguete.",
          "Solo leer el manual de instrucciones.",
          "Registrarse como operador de UAS y poner su número de registro en el dron.",
          "Obtener la certificación avanzada A2.",
        ],
        c: 2,
        exp: "Cualquier dron equipado con un sensor capaz de captar datos personales (como una cámara) obliga a su propietario a registrarse como operador de UAS en AESA y colocar su número de operador en la aeronave, independientemente de que pese menos de 250g.",
      },
      {
        q: "En la subcategoría A3, ¿a qué distancia mínima debes volar de zonas residenciales, comerciales, industriales o recreativas?",
        o: [
          "50 metros",
          "150 metros",
          "30 metros",
          "Se puede sobrevolar si no hay personas debajo",
        ],
        c: 1,
        exp: "Las operaciones en la subcategoría A3 deben realizarse a una distancia horizontal mínima de 150 metros de zonas residenciales, comerciales, industriales o recreativas.",
      },
    ],
  },

  pricing: {
    eyebrow: "Precio único",
    eyebrowTitle1: "Suscripción plana",
    eyebrowTitle2: "Vuelos ilimitados",
    eyebrowSubtitle:
      "Acceso total a los cuestionarios A1/A3 y A2. Paga solo mientras te preparas.",
    stickyNote: "Sin permanencia",
    anchorPrice: null,
    reassurance: [
      { label: "Pago seguro con Stripe", iconKey: "stripe" },
      { label: "Cancela cuando quieras", iconKey: "no-lock" },
      { label: "Acceso inmediato", iconKey: "check" },
    ],
    plan: {
      name: "Plan AESA Pass",
      price: "14,99 €",
      currency: "",
      period: "/mes",
      priceSubtext: "Garantiza tu licencia europea",
      description:
        "Acceso completo a todos los temarios, simulacros A1/A3/A2 y herramientas de IA.",
      features: [
        "Banco de preguntas actualizado (A1/A3 y A2)",
        "Explicaciones basadas en Reglamentos UE",
        "Simulacros reales de AESA (40 preguntas)",
        "Instructor IA especializado en UAS",
        "Métricas de progreso por área de conocimiento",
        "Soporte técnico prioritario",
      ],
      ctaLabel: "Despegar Ahora",
      benefitsList: [
        "Simulacros idénticos a los oficiales",
        "Práctica ilimitada",
        "+800 preguntas de normativa y técnica",
        "Instructor IA experto 24/7",
        "Estudia en móvil, tablet o PC",
        "Temario oficial AESA/EASA cubierto",
        "Estadísticas de vuelo",
        "Soporte prioritario",
      ],
    },
  },

  faq: {
    eyebrow: "Preguntas Frecuentes",
    title: "Aclara tus dudas de vuelo",
    subtitle: "Lo que la mayoría de aspirantes pregunta antes de comenzar.",
    items: [
      {
        question: "¿Necesito tener un dron para realizar el curso y el examen?",
        answer:
          "No, la certificación oficial teórica A1/A3 y A2 de AESA se puede obtener antes de comprar tu primer dron, lo cual es altamente recomendado para conocer las limitaciones legales.",
      },
      {
        question: "¿El temario está actualizado a la última normativa europea?",
        answer:
          "Sí. Nuestras preguntas reflejan los Requisitos Generales, el Reglamento de Ejecución (UE) 2019/947 y el Reglamento Delegado (UE) 2019/945, que son los exigidos por AESA y EASA.",
      },
      {
        question: "¿Puedo cancelar mi suscripción cuando apruebe el examen?",
        answer:
          "Por supuesto. La mayoría de los usuarios aprueban en su primer o segundo mes. Puedes cancelar tu suscripción con un solo clic desde tu panel y no se te volverá a cobrar.",
      },
      {
        question: "¿Los test sirven para la subcategoría A2?",
        answer:
          "Sí, nuestra plataforma incluye la base de datos de preguntas tanto para el nivel básico A1/A3 como para el examen presencial/proctorizado de la categoría A2 (meteorología avanzada, rendimiento de vuelo y mitigaciones técnicas).",
      },
      {
        question: "¿Esta plataforma me entrega el certificado oficial?",
        answer:
          "No. TestDrones es un simulador de preparación intensiva. El examen oficial y la emisión del certificado final las realiza exclusivamente AESA a través de su portal oficial.",
      },
    ],
  },

  platformIncludes: {
    eyebrow: "El simulador",
    title: "Todo tu equipaje en regla",
    subtitle:
      "Las herramientas necesarias para superar el test de AESA sin frustraciones.",
    items: [
      {
        icon: "📚",
        iconKey: "book",
        title: "+800 preguntas explicadas",
        description:
          "No solo te decimos si acertaste, te explicamos el artículo de la ley europea que justifica la respuesta.",
      },
      {
        icon: "⏱️",
        iconKey: "clock",
        title: "Simulacros de examen AESA",
        description:
          "Generación de exámenes de 40 preguntas en 45 minutos (estándar A1/A3) para que te adaptes a la presión del reloj.",
      },
      {
        icon: "🚁",
        iconKey: "document-check",
        title: "Casos Operacionales",
        description:
          "Preguntas basadas en escenarios reales de vuelo, distancias a aeropuertos e infraestructuras críticas.",
      },
      {
        icon: "📊",
        iconKey: "chart",
        title: "Radar de progreso",
        description:
          "Dashboard intuitivo para saber si dominas meteorología pero te falta repasar protección de datos.",
      },
    ],
  },

  trustStrip: [
    { icon: "🇪🇺", iconKey: "flag", label: "Normativa Europea EASA 2024" },
    { icon: "🔒", iconKey: "lock", label: "Pago seguro con Stripe" },
    {
      icon: "🚫",
      iconKey: "no-lock",
      label: "Sin permanencia · Cancelación en 1 clic",
    },
    { icon: "🚁", iconKey: "document-check", label: "Categorías A1/A3 y A2" },
  ],

  howItWorks: {
    eyebrow: "Ruta de Vuelo",
    title: "Consigue tu licencia en 3 pasos",
    subtitle:
      "Deja de leer boletines aburridos y empieza a practicar de forma inteligente.",
    steps: [
      {
        icon: "✨",
        iconKey: "spark",
        title: "Prueba los controles",
        description:
          "Realiza un simulacro gratuito desde esta página para medir tu conocimiento actual sobre drones y espacio aéreo.",
      },
      {
        icon: "📝",
        iconKey: "clipboard",
        title: "Crea tu cuenta",
        description:
          "Regístrate gratis para acceder a la estructura del temario y empezar a guardar tu historial de rendimiento.",
      },
      {
        icon: "🏆",
        iconKey: "trophy",
        title: "Vuélvete premium y aprueba",
        description:
          "Desbloquea el simulador completo, las explicaciones de la normativa y la ayuda del Instructor IA.",
      },
    ],
  },

  comparison: {
    eyebrow: "Por qué TestDrones",
    title: "Volar seguro vs. Volar a ciegas",
    subtitle:
      "La diferencia entre presentarse con confianza o dudar frente a la pantalla de AESA.",
    withLabel: "Con TestDrones",
    withoutLabel: "Por tu cuenta",
    rows: [
      { label: "Normativa resumida y testeable", withUs: true, without: false },
      {
        label: "Simulacros cronometrados reales",
        withUs: true,
        without: false,
      },
      {
        label: "Explicación de cada regla",
        withUs: true,
        without: "Foros dudosos de internet",
      },
      { label: "Medición de puntos débiles", withUs: true, without: false },
      { label: "Instructor IA experto en EASA", withUs: true, without: false },
      {
        label: "Inversión requerida",
        withUs: "14,99 €/mes, sin contratos",
        without: "Academias desde 100 €",
      },
    ],
  },

  ai: {
    chatTitle: "Instructor IA TestDrones",
    welcomeMessage:
      "¡Hola! Soy tu Instructor experto en normativa UAS de AESA y EASA. ¿Tienes dudas sobre distancias de vuelo, zonas CTR, clases de drones (C0 a C4) o categorías operacionales? Pregúntame.",
    systemInstructionDefault:
      "Eres un experto en aeronáutica y normativa de Drones (UAS) en España regulada por AESA y EASA (Reglamentos UE 2019/947 y 2019/945). Conoces perfectamente las categorías Abierta (A1/A3, A2) y Específica, meteorología básica, limitaciones del espacio aéreo (CTR, ATZ, zonas ZEPA) y privacidad. Responde en español de España (es-ES), de forma concisa y técnica. No inventes artículos ni datos.",
    systemInstructionTutor:
      "Eres un instructor de vuelo de drones experto en superar los exámenes de AESA. Eres directo, claro y didáctico. Si explicas una regla, cita si es posible la categoría o clase aplicable. Responde en español (es-ES).",
    casePromptTemplate: (topicTitle) =>
      `Genera un caso práctico corto (máximo 100 palabras) sobre "${topicTitle}" en el que un operador o piloto a distancia está realizando un vuelo con su dron en España. Pregunta cuál sería la acción legal y segura que debe tomar según EASA/AESA.`,
    caseSystemInstruction:
      "Eres un examinador oficial de AESA especializado en UAS (drones).",
    inputPlaceholder: "Escribe tu duda sobre normativas o vuelo...",
  },

  exam: {
    simulatorQuestions: 40, // Formato estándar de A1/A3 en AESA
    simulatorDurationMinutes: 45,
    // ATENCIÓN: Estos IDs deben coincidir con tu nuevo archivo temario.js para drones
    // Ejemplo: t1=Reglamentación, t2=Limitaciones, t3=Procedimientos, etc.
    topicWeights: [
      { id: "t1", weight: 0.15 },
      { id: "t2", weight: 0.15 },
      { id: "t3", weight: 0.15 },
      { id: "t4", weight: 0.15 },
      { id: "t5", weight: 0.1 },
      { id: "t6", weight: 0.1 },
      { id: "t7", weight: 0.1 },
      { id: "t8", weight: 0.1 },
    ],
    passingScore: 75, // AESA suele pedir el 75% para aprobar
    simulatorTitle: "Simulacro Oficial AESA",
    exitModal: {
      title: "¿Abortar vuelo?",
      lead: "¡Tus datos de telemetría se perderán!",
      warning:
        "Las respuestas que has marcado no se guardarán en la caja negra y tendrás que iniciar el examen de nuevo.",
      confirmLabel: "Sí, abortar",
      cancelLabel: "Continuar vuelo",
      footnote: "Tu progreso se restablecerá si sales ahora",
      closeLabel: "Cerrar",
    },
  },

  uiCopy: {
    loading: "Cargando telemetría...",
    ready: "Sistemas listos",
    navHome: "Base de Operaciones",
    navStudy: "Reglamentación",
    navStudyShort: "Temario",
    navQuizzes: "Módulos de Vuelo",
    navQuizzesShort: "Módulos",
    navSimulator: "Simulacro Real",
    navBilling: "Mi Plan",
    sidebarPrincipal: "Principal",
    sidebarSimulation: "Simulación",
    bannerCta: "🚀 Desbloquea los Simulacros Reales y asegura tu Certificado",
    bannerCtaButton: "Premium",
    legalLastUpdate: "Última actualización: 15 de enero de 2026",
    cta: {
      finalSectionTitle:
        "¿Listo para obtener tu certificado de Piloto de Drones?",
      finalSectionSubtitle:
        "Prepara los exámenes oficiales A1/A3 y A2 con +800 preguntas de la normativa EASA. Empieza a volar hoy.",
      finalSectionPrimary: "Iniciar preparación",
      finalSectionSecondary: "Contactar soporte",
      finalSectionFooter: "✨ Acceso inmediato · Cancela cuando quieras",
      contactModalTitle: "Torre de Control",
      contactModalSubtitle: "¿Problemas en el radar? Escríbenos directamente:",
      contactModalClose: "Cerrar ventana",
      contactModalCloseAriaLabel: "Cerrar",
    },
    masterDomainLabel: "Dominio de Normativa UAS",
    statsCards: {
      generalProgress: "Progreso Total",
      sectionsRead: "Módulos Completados",
      examsTaken: "Vuelos de Prueba (Simulacros)",
      bestScore: "Puntaje Máximo",
    },
    auth: {
      welcomeTitle: "Bienvenido, Piloto",
      welcomeSubtitle: "Inicia sesión para acceder a tu panel de vuelo",
      registerTitle: "Registro de Operador",
      registerSubtitle: "Da el primer paso hacia tu certificación oficial",
    },
    landing: {
      productNavLabel: "Plataforma",
      productLinks: [
        { href: "#features", label: "Características" },
        { href: "#pricing", label: "Planes" },
        { href: "#demo", label: "Prueba Gratuita" },
        { href: "#testimonials", label: "Equipamiento" },
      ],
    },
  },

  legal: {
    companyName: "TestDrones by MIVX",
    companyShortName: "TestDrones",
    governingLaw: "España",
    privacyContactEmail: "go@mivx.com",
    operatorName: "PAUL BREMOND SAS",
    operatorLocation: "París, Francia",
  },
};

export default siteConfig;
